import { LoadingSpinner } from "components/LoadingSpinner";
import { useLoading } from "context/LoadingContext";
import { ToastContext } from "context/ToastContext";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { confirmDialog } from "primereact/confirmdialog";
import { DataTable } from "primereact/datatable";
import { InputSwitch } from "primereact/inputswitch";
import { useMemo, useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import {
  useDeleteInpatientRulesetMutation,
  useGetInpatientRulesetsQuery,
} from "store/queries/inpatientRulesets";
import styled from "styled-components";

import { defaultScopeCell } from "./defaultScopeCell";

const Row = styled.div`
  display: flex;
  flex-direction: row;
`;

const ColumnWrapper = styled.div`
  display: block;
  max-height: 20px;
  width: 100%;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

const ToolbarRow = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 0.5rem;
`;

const TYPE_LABELS: Record<string, string> = {
  FIXED_COST: "Fixed Cost",
  PASS_THROUGH: "Pass Through",
  EXCESSIVE_LOS: "Excessive LOS",
  TRANSFER: "Transfer",
  STOP_LOSS: "Stop Loss",
  CHARGE_OUTLIER: "Charge Outlier",
  INPATIENT_OUTLIER: "Inpatient Outlier Rules",
};

export function InpatientRulesetsTable() {
  const [selectedItems, setSelectedItems] = useState<{ id: string }[]>([]);
  const [showDefaultsOnly, setShowDefaultsOnly] = useState(false);
  const { setIsLoading } = useLoading();
  const { current: toastElement } = useContext(ToastContext);
  const [deleteRuleset] = useDeleteInpatientRulesetMutation();

  const navigate = useNavigate();
  const {
    data: rulesets,
    isLoading,
    refetch,
  } = useGetInpatientRulesetsQuery(
    showDefaultsOnly ? { isDefault: true } : undefined
  );

  const parsedRulesets = useMemo(() => {
    if (!rulesets?.data) return [];
    return rulesets.data.map((ruleset) => ({
      ...ruleset,
      providerName: ruleset.provider?.name ?? "—",
      payerName: ruleset.payer?.name ?? "—",
      typeLabel: TYPE_LABELS[ruleset.type] || ruleset.type,
    }));
  }, [rulesets]);

  const drgCodesTransformer = (drgCodes: string[]) => {
    const allCodes = drgCodes.join(", ");
    return <ColumnWrapper>{allCodes}</ColumnWrapper>;
  };

  function rulesetAction(rulesetId: string, copy?: boolean) {
    return (
      <Row>
        <Button
          className="p-button-link"
          onClick={() =>
            navigate(
              `/payer-rules/inpatient-rulesets/${
                copy ? "copy" : "edit"
              }/${rulesetId}`
            )
          }
        >
          <p className="small-text">{copy ? "Copy" : "Edit"}</p>
        </Button>
      </Row>
    );
  }

  const handleDelete = async () => {
    setIsLoading(true);
    try {
      const deletePromises = selectedItems.map((item) =>
        deleteRuleset(item.id)
      );
      await Promise.all(deletePromises);
      await refetch();
      toastElement?.show({
        severity: "success",
        summary: "Deleted!",
        detail: "Inpatient ruleset(s) deleted successfully.",
      });
    } catch (error) {
      await refetch();
      toastElement?.show({
        severity: "error",
        summary: "Error",
        detail: "Error while deleting inpatient rulesets",
      });
    } finally {
      setIsLoading(false);
      setSelectedItems([]);
    }
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <>
      <ToolbarRow>
        <span style={{ fontSize: 13 }}>Show defaults only</span>
        <InputSwitch
          checked={showDefaultsOnly}
          onChange={(e) => setShowDefaultsOnly(!!e.value)}
        />
      </ToolbarRow>
      <DataTable
        rows={10}
        paginator
        stripedRows
        className="p-fluid"
        filterDisplay="row"
        value={parsedRulesets}
        responsiveLayout="scroll"
        style={{ cursor: "pointer" }}
        selection={selectedItems}
        emptyMessage="No inpatient rulesets to show."
        selectionMode="single"
        onSelectionChange={(event) => setSelectedItems(event.value)}
      >
        <Column selectionMode="multiple" headerStyle={{ width: "3em" }} />
        <Column
          filter
          field="name"
          style={{ minWidth: "200px" }}
          header="Name"
          filterPlaceholder="Search by name"
        />
        <Column
          filter
          field="providerName"
          style={{ minWidth: "250px" }}
          header="Provider Name"
          filterPlaceholder="Search by provider"
          body={(row) => defaultScopeCell(row, row.providerName)}
        />
        <Column
          filter
          field="payerName"
          style={{ minWidth: "250px" }}
          header="Payer Name"
          filterPlaceholder="Search by payer"
          body={(row) => defaultScopeCell(row, row.payerName)}
        />
        <Column field="typeLabel" style={{ minWidth: "120px" }} header="Type" />
        <Column
          filter
          field="drgCodes"
          body={(row) => drgCodesTransformer(row.drgCodes)}
          style={{ minWidth: "250px", maxWidth: "280px" }}
          header="DRG Codes"
          filterPlaceholder="Search by DRG code"
        />
        <Column
          style={{ maxWidth: "100px" }}
          body={(row) => rulesetAction(row.id, true)}
        />
        <Column
          style={{ maxWidth: "100px" }}
          body={(row) => rulesetAction(row.id)}
        />
      </DataTable>
      <div className="flex justify-content-end">
        <Button
          label="Delete"
          className="p-button-outlined mt-4"
          disabled={!selectedItems.length}
          onClick={() => {
            confirmDialog({
              header: "Confirmation",
              icon: "pi pi-exclamation-triangle",
              message:
                "Are you sure you want to delete selected inpatient rulesets?",
              accept: handleDelete,
            });
          }}
        />
      </div>
    </>
  );
}
